import { Box, Button, MenuItem, TextField } from '@mui/material';
import { Field, Form, Formik, useFormikContext } from 'formik';
import { ContentCard } from 'components/Card';
import { getTrans, TranslationKey } from 'modules/i18n';
import { LanguageSkillEntry } from 'utils/cv-form-db';
import { LANGUAGE_LEVEL_OPTIONS, LANGUAGE_OPTIONS } from './constants';

interface LanguageSkillsFormProps {
  mode: 'add' | 'edit';
  initialValues?: LanguageSkillEntry;
  onSubmit: (values: LanguageSkillEntry) => void;
  onCancel: () => void;
}

const ButtonContainer = ({ children }: { children: React.ReactNode }) => (
  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>{children}</Box>
);

const SaveLanguageSkillButton = ({ mode }: { mode: 'add' | 'edit' }) => (
  <Button
    type="submit"
    variant="contained"
    color="secondary"
    fullWidth
    data-testid={`${mode}-language-skill-save-button`}
  >
    {getTrans(
      mode === 'edit'
        ? 'general.save'
        : 'cvBuilderPage.languageSkills.form.addButton'
    )}
  </Button>
);

const CancelLanguageSkillButton = ({ onCancel }: { onCancel: () => void }) => (
  <Button
    variant="outlined"
    color="secondary"
    onClick={onCancel}
    fullWidth
    data-testid="cancel-language-skill-button"
  >
    {getTrans('general.cancel')}
  </Button>
);

const InnerLanguageSkillsForm = ({
  mode = 'add',
  onCancel
}: {
  mode: 'add' | 'edit';
  onCancel: () => void;
}) => {
  useFormikContext<LanguageSkillEntry>();

  const isEditMode = mode === 'edit';

  return (
    <Form
      data-testid={`${isEditMode ? 'edit' : 'add'}-language-skill-form`}
      style={{
        flexBasis: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <ContentCard>
        <Field
          as={TextField}
          name="language"
          label={getTrans('cvBuilderPage.languageSkills.form.language.label')}
          fullWidth
          select
          required
          sx={{ mb: 2 }}
        >
          {LANGUAGE_OPTIONS.map(option => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Field>

        <Field
          as={TextField}
          name="level"
          label={getTrans('cvBuilderPage.languageSkills.form.level.label')}
          fullWidth
          select
          required
          sx={{ mb: 2 }}
        >
          {LANGUAGE_LEVEL_OPTIONS.map(option => (
            <MenuItem key={option.key} value={option.key}>
              {getTrans(option.value as TranslationKey)}
            </MenuItem>
          ))}
        </Field>

        <Box
          sx={{
            backgroundColor: 'primary.lighter',
            p: 2,
            borderRadius: 1,
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: 'primary.main',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1
              }}
            >
              <Box
                sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}
              >
                i
              </Box>
            </Box>
            <Box sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
              {getTrans('cvBuilderPage.languageSkills.form.infoCard.title')}
            </Box>
          </Box>
          <Box sx={{ fontSize: '14px', color: 'text.secondary' }}>
            {LANGUAGE_LEVEL_OPTIONS.map((option, index) => (
              <Box key={option.key}>
                {getTrans(option.value as TranslationKey)}
                {index < LANGUAGE_LEVEL_OPTIONS.length - 1 && ', '}
              </Box>
            ))}
          </Box>
        </Box>
      </ContentCard>

      <ButtonContainer>
        <SaveLanguageSkillButton mode={mode} />
        <CancelLanguageSkillButton onCancel={onCancel} />
      </ButtonContainer>
    </Form>
  );
};

export const LanguageSkillsForm = ({
  mode = 'add',
  initialValues,
  onSubmit,
  onCancel
}: LanguageSkillsFormProps) => {
  const defaultValues: LanguageSkillEntry = {
    language: '',
    level: ''
  };

  return (
    <Formik
      initialValues={initialValues || defaultValues}
      onSubmit={onSubmit}
      enableReinitialize
    >
      <InnerLanguageSkillsForm mode={mode} onCancel={onCancel} />
    </Formik>
  );
};
